import {
    Alert,
    Button,
    Checkbox,
    DatePicker,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Radio,
    Select,
    Space,
} from "antd";
import type { ShippingPriceIndex, ShippingRequirementCustomerForm, ShippingRequirementCustomerVo, ShippingRequirementPlat, ShipRoute } from "@zhihaoscm/entity";
import {
    DRAINAGE_CHANNEL,
    DEPOSIT_PAYMENT_METHOD,
    FIND_SHIP_TYPE,
    FREIGHT_RATE_TYPE,
    MARITIME_AFFAIRS_FEE,
    SERVICE_PROVIDER_TYPE_PC,
    SHIP_REQUIREMENTS,
    SHIO_TOP_DEMAND,
    SHIP_INFO_SERVICE_FEE_DEF,
    SHIPPING_REQUIREMENT_CUSTOMER_TRANSPORT_ORDER_STATE,
    SHIP_WAY_BILL_TYPE_PC,
    PERMISSION,
    TRACK_TYPE
} from "@zhihaoscm/meta";
import dayjs from "dayjs";
import sensors from "sa-sdk-javascript";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { shipRequirementService } from "@/api/shipRequireService";
import { transportOrderService } from "@/api/transportOrderService";
import { serviceProviderService } from "@/api/serviceProviderService";
import { fileService } from "@/api/fileService";
import { shipRouteServcie } from "@/api/shipRouteService";
import { indexFreightService } from "@/api/indexFreight";
import { customerService } from "@/api/customerService";
import { ProTable, type ProColumn } from "@/components/common";
import { Breadcrumb, DetailHeader, ZhInsetTabs } from "@/components/common";
import { InTransitPanel } from "./InTransitPanel";
import { ShipOrderBaseInfo, ShipOrderCancelInfo, ShipOrderOperationHistory, ShipRequirementInfo } from "./ShipOrderDetailSections";
import { getShipOrderStage, ShipOrderDepartureInfo, ShipOrderDischargedInfo, ShipOrderLoadedInfo, ShipOrderMonitor, ShipOrderTransportationInfo } from "./ShipOrderStageSections";
import { ShipOrderActions } from "./ShipOrderActions";
import { ShipCommonGoods, ShipPaymentIntroduction, ShipPriceIndexCard, ShipProviderRadio, ShipRemotePortSelect, type ProviderCard } from "./ShipOrderFormLegacyComponents";
import { AccountType, useAccountSession, useUserSession } from "@/stores/session";
import "./ship-order.css";

/** 旧版 formatCurrency：列表中的吨数、运价统一千分位，空值显示占位符。 */
const formatListNumber = (value: unknown) =>
    value === null || value === undefined || value === "" ? "--" : Number(value).toLocaleString("en-US");
/** 需求阶段仍读取 plat，生成正式运单后读取 orderShips。 */
const isRequirementStage = (state: number) => [5, 6, 7, 15].includes(state);
/** Vue TRANSPORT_ORDER_CUSTOM_STATE：状态 8 由“定金确认中”状态 1 一并查询，不单独出现在下拉框。 */
const shipOrderStateOptions = [
    [6, "处理中"],
    [7, "找船中"],
    [1, "定金确认中"],
    [3, "待发航"],
    [11, "运输中"],
    [4, "待卸货"],
    [13, "已完成"],
    [14, "未完成"],
    [15, "已取消"]
].map(([value, label]) => ({ value: Number(value), label: String(label) }));

/** 运单列表兼容找船需求和尚未结束的正式船运单。 */
export function ShipOrderListPage() {
    const navigate = useNavigate();
    const accountType = useAccountSession(state => state.accountType);
    const permissions = useUserSession(state => (state.userInfo as any).permissions as number[] | undefined);
    const canCreate = accountType !== AccountType.SUB_ACCOUNT || Boolean(permissions?.includes(PERMISSION.LOGISTICS.code));
    const [reloadVersion, setReloadVersion] = useState(0);
    const refreshList = useCallback(async () => setReloadVersion(version => version + 1), []);
    /** 标题链接严格复用 Vue onDetail 的 id/requireId 选择顺序。 */
    const openDetail = useCallback((row: ShippingRequirementCustomerVo) => {
        const orderId = row.orderShips?.[0]?.id ?? row.orderId;
        navigate(`/logistics/shipOrder/detail?${orderId ? `id=${orderId}` : `requireId=${row.plat?.id}`}`);
    }, [navigate]);
    const columns = useMemo<ProColumn<ShippingRequirementCustomerVo>[]>(
        () => [
            {
                key: "title",
                title: "运单编号",
                width: 198,
                search: { key: "title", type: "input", placeholder: "请输入运单编号搜索", order: 1, span: 5, width: 120 },
                render: (_, row) => (
                    <button className="ship-order-list__link" type="button" onClick={() => openDetail(row)}>
                        {row.title}
                    </button>
                )
            },
            { key: "sourcePortName", title: "始发地", minWidth: 142, search: { key: "sourcePortName", type: "input", placeholder: "请输入始发地关键词搜索", order: 3, span: 4, width: 120 } },
            { key: "destinationPortName", title: "目的地", minWidth: 142, search: { key: "destinationPortName", type: "input", placeholder: "请输入目的地关键词搜索", order: 4, span: 4, width: 120 } },
            { key: "shipName", title: "船舶名称", minWidth: 142, search: { key: "shipKey", type: "input", placeholder: "请输入船舶名称关键词搜索", order: 2, span: 4, width: 120 }, render: (_, row) => row.shipName || "--" },
            {
                key: "ton",
                title: "运货吨数/卸货吨位",
                width: 170,
                align: "right",
                headerAlign: "right",
                render: (_, row) => (
                    <span>
                        {formatListNumber(isRequirementStage(row.tarnsPortOrderState) ? row.plat?.freightTons : row.orderShips?.[0]?.ton)}
                        <i className="pro-table-gap">|</i>
                        <em className={!isRequirementStage(row.tarnsPortOrderState) && row.orderShipVos?.[0]?.transportOrderDetailsShip?.unloadingInfo?.unloadingTonnage ? "table-highlight" : undefined}>
                            {isRequirementStage(row.tarnsPortOrderState) ? "--" : formatListNumber(row.orderShipVos?.[0]?.transportOrderDetailsShip?.unloadingInfo?.unloadingTonnage)}
                        </em>
                    </span>
                )
            },
            {
                key: "unitPrice",
                title: "意向运价/结算运价（元/吨）",
                width: 220,
                align: "right",
                headerAlign: "right",
                render: (_, row) => (
                    <span>
                        {FREIGHT_RATE_TYPE.CALL_FOR_PRICE.match(row.plat?.unitPriceType)
                            ? FREIGHT_RATE_TYPE.getIntroByCode(row.plat?.unitPriceType)
                            : formatListNumber(row.plat?.unitPrice)}
                        <i className="pro-table-gap">|</i>
                        <em className={row.orderShips?.[0]?.unitPrice ? "table-highlight" : undefined}>{isRequirementStage(row.tarnsPortOrderState) ? "--" : formatListNumber(row.orderShips?.[0]?.unitPrice)}</em>
                    </span>
                )
            },
            {
                key: "loadDate",
                title: "装载日期",
                width: 170,
                render: (_, row) => {
                    const source = isRequirementStage(row.tarnsPortOrderState) ? row.plat : row.orderShips?.[0];
                    const date = source?.loadDate ? dayjs(source.loadDate).format("YYYY-MM-DD") : "--";
                    return `${date} +${source?.loadDays || "--"}天`;
                }
            },
            {
                key: "ownerShipInfoServiceFeeState",
                title: "信息服务费支付状态",
                width: 170,
                search: {
                    key: "ownerShipInfoServiceFeeState",
                    type: "select",
                    placeholder: "请选择信息服务费支付状态",
                    order: 5,
                    span: 3,
                    width: 80,
                    options: SHIP_INFO_SERVICE_FEE_DEF.options.map(item => ({
                        label: item.label,
                        value: Number(item.value)
                    }))
                },
                render: (_, row) =>
                    [6, 7, 8].includes(row.tarnsPortOrderState)
                        ? "--"
                        : row.orderShips?.[0]?.ownerShipInfoServiceFeeState
                        ? <span className={SHIP_INFO_SERVICE_FEE_DEF.UNPAID.match(row.orderShips[0].ownerShipInfoServiceFeeState) ? "ship-order-list__unpaid" : undefined}>{SHIP_INFO_SERVICE_FEE_DEF.getIntroByCode(row.orderShips[0].ownerShipInfoServiceFeeState)}</span>
                        : "--"
            },
            {
                key: "state",
                title: "运单状态",
                width: 140,
                enum: shipOrderStateOptions,
                search: { key: "states", type: "select", placeholder: "请选择运单状态", options: shipOrderStateOptions, order: 6, multiple: true, span: 3, width: 80 },
                render: (_, row) => (
                    <div>
                        <span>
                            {SHIPPING_REQUIREMENT_CUSTOMER_TRANSPORT_ORDER_STATE.getIntroByCode(
                                row.tarnsPortOrderState
                            )}
                        </span>
                        {row.quoteCount && [7, 15].includes(row.tarnsPortOrderState) ? (
                            <small className={row.tarnsPortOrderState === 15 ? "quote-count-history" : "quote-count"}>
                                {row.tarnsPortOrderState === 15 ? "历史" : "当前"}
                                {row.quoteCount}人报价
                            </small>
                        ) : null}
                    </div>
                )
            },
            {
                key: "serviceType",
                title: "服务类型",
                width: 140,
                search: {
                    key: "serviceType",
                    type: "select",
                    placeholder: "请选择服务类型",
                    order: 7,
                    span: 3,
                    width: 80,
                    options: SHIP_WAY_BILL_TYPE_PC.options.map(item => ({
                        label: item.label,
                        value: Number(item.value)
                    }))
                },
                render: (_, row) =>
                    SHIP_WAY_BILL_TYPE_PC.getIntroByCode(row.orderShips?.[0]?.serviceType ?? row.plat?.serviceType) ||
                    "--"
            },
            { key: "publishTime", title: "发布时间", width: 170, sortable: true, render: (_, row) => row.publishTime ? dayjs(row.publishTime).format("YYYY-MM-DD HH:mm") : "--" },
            {
                key: "operation",
                title: "操作",
                width: 280,
                fixed: "right",
                render: (_, row) => <ShipOrderActions info={{ ...row, orderShips: row.orderShips ?? [], orderShipVos: row.orderShipVos ?? [] }} mode="list" refresh={refreshList} onRepeat={id => navigate(`/logistics/shipOrder/edit?id=${id}`)} />
            }
        ],
        [navigate, openDetail, refreshList]
    );
    return (
        <section className="section-card ship-order-list">
            <Breadcrumb routes={[{ name: "物流中心" }, { name: "运单" }]} />
            <div className="table-box">
                <ProTable
                    key={reloadVersion}
                    title="运单列表"
                    rowKey={row => row.orderId || row.plat?.id || row.title}
                    columns={columns}
                    request={params => {
                        const states = Array.isArray(params.states) ? params.states.map(Number) : undefined;
                        // 对齐 Vue getTableList：状态 1 同时查询自行支付和服务商支付两种定金确认状态。
                        const mappedStates = states?.includes(1) ? [...states.filter(state => state !== 1), 1, 8] : states;
                        return shipRequirementService.getOrderPaging({ ...params, states: mappedStates } as never);
                    }}
                    searchPrefix={() => canCreate ? (
                        <Button type="primary" onClick={() => {
                            sensors.track("169", { type: TRACK_TYPE.ELEMENT_CLICK.code });
                            navigate("/logistics/shipOrder/edit");
                        }}>
                            新增找船
                        </Button>
                    ) : null}
                />
            </div>
        </section>
    );
}

interface OrderFormValues extends Omit<ShippingRequirementCustomerForm, "loadDate"> {
    loadDate: dayjs.Dayjs;
    paymentMethod: number;
}
/** 新增找船需求覆盖服务、货物、港口、运价、装载和联系人核心字段。 */
export function ShipOrderFormPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const repeatOrderId = searchParams.get("id") ?? "";
    const currentCustomerId = useUserSession(state => state.userInfo.customer?.id);
    const currentMobile = useUserSession(state => state.mobile);
    const [form] = Form.useForm<OrderFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [commonGoods, setCommonGoods] = useState<string[]>([]);
    const [providers, setProviders] = useState<ProviderCard[]>([]);
    const [lastOrder, setLastOrder] = useState<ShippingRequirementPlat>();
    const [showIntroduction, setShowIntroduction] = useState(false);
    const [routes, setRoutes] = useState<ShipRoute[]>([]);
    const [priceIndex, setPriceIndex] = useState<ShippingPriceIndex>();
    const serviceType = Form.useWatch("serviceType", form);
    const paymentMethod = Form.useWatch("paymentMethod", form);
    const depositPaymentMethod = Form.useWatch("depositPaymentMethod", form);
    const sourcePortId = Form.useWatch("sourcePortId", form);
    const destinationPortId = Form.useWatch("destinationPortId", form);
    const shipTonDemand = Form.useWatch("shipTonDemand", form);
    const unitPrice = Form.useWatch("unitPrice", form);
    const ton = Form.useWatch("ton", form);
    const unitPriceType = Form.useWatch("unitPriceType", form);
    useEffect(() => {
        /** 三个 Vue 直接子组件各自的初始化请求在 React 页进入时并行完成。 */
        void Promise.all([transportOrderService.topThreeGoods(), serviceProviderService.configFind(), shipRequirementService.importLastData()]).then(async ([goods, configs, previous]) => {
            setCommonGoods(goods);
            setLastOrder(previous);
            const opened = configs.filter(item => item.hasOpen);
            const ids = opened.map(item => item.pictureTextInfo.pictureId).filter(Boolean);
            const files = ids.length ? await fileService.getFileByIds(ids) : [];
            setProviders(opened.map(item => ({ ...item, pictureTextInfo: { ...item.pictureTextInfo, path: files.find(file => file.id === item.pictureTextInfo.pictureId)?.path } })));
        });
    }, []);
    useEffect(() => {
        if (currentMobile) form.setFieldValue("mobile", currentMobile);
        if (currentCustomerId) void customerService.getCustomerRealName(currentCustomerId).then(contact => form.setFieldValue("contact", contact));
    }, [currentCustomerId, currentMobile, form]);
    /** 航线选择后同步旧版的航线首尾字段和最新指数数据。 */
    const selectRoute = useCallback(async (route: ShipRoute) => {
        form.setFieldsValue({
            shipRouteId: route.id,
            shipRouteSource: route.source,
            shipRouteDestination: route.destination
        } as never);
        setPriceIndex(await indexFreightService.findNewestDataByShipRouteId(route.id));
    }, [form]);
    useEffect(() => {
        if (!sourcePortId || !destinationPortId) {
            setRoutes([]);
            setPriceIndex(undefined);
            return;
        }
        void shipRouteServcie.getShipRoute(sourcePortId, destinationPortId).then(async list => {
            setRoutes(list);
            if (!list.length) {
                setPriceIndex(undefined);
                form.setFieldsValue({ shipRouteId: undefined, shipRouteSource: undefined, shipRouteDestination: undefined } as never);
                void message.error("没有此条航线运价");
                return;
            }
            if (list.length === 1) await selectRoute(list[0]);
        });
    }, [destinationPortId, form, selectRoute, sourcePortId]);

    /** 将旧需求或正式运单转换回新增页表单字段，供“再来一单”和“导入上次数据”共同使用。 */
    const importOrderValues = useCallback((plat?: ShippingRequirementPlat, transport?: any) => {
        const source = transport ?? plat;
        if (!source) return;
        const sourcePlat = plat ?? ({} as ShippingRequirementPlat);
        const importedValues = {
            serviceType: source.serviceType ?? SHIP_WAY_BILL_TYPE_PC.SUPERVISION.code,
            paymentMethod: source.payType === DEPOSIT_PAYMENT_METHOD.PAY_ONESELF.code
                ? SERVICE_PROVIDER_TYPE_PC.SELF.code
                : SERVICE_PROVIDER_TYPE_PC.SERVICE.code,
            depositPaymentMethod: source.payType,
            orderType: 1,
            goodsType: source.goodsType,
            sourcePortId: source.sourcePortId,
            sourcePortName: source.sourcePortName,
            destinationPortId: source.destinationPortId,
            destinationPortName: source.destinationPortName,
            shipRouteId: source.shipRouteId,
            shipRouteSource: source.shipRouteSource,
            shipRouteDestination: source.shipRouteDestination,
            unitPrice: source.unitPrice,
            unitPriceType: source.unitPriceType,
            ton: transport ? source.ton : source.freightTons,
            loadDate: source.loadDate ? dayjs(source.loadDate) : undefined,
            loadDays: source.loadDays,
            loadUnloadDays: source.loadUnloadDays,
            demurrageFee: source.demurrageFee,
            maritimeAffairsFee: source.maritimeAffairsFee,
            deposit: source.deposit,
            replenishAppoint: sourcePlat.replenishAppoint || "",
            drainageChannel: sourcePlat.drainageChannel == null || DRAINAGE_CHANNEL.NO_DRAINAGE_CHANNEL.match(sourcePlat.drainageChannel) ? undefined : sourcePlat.drainageChannel,
            shipRequirements: sourcePlat.shipRequirements || [],
            dwtMax: sourcePlat.tonMax || undefined,
            dwtMin: sourcePlat.tonMin || undefined,
            shipType: sourcePlat.shipType || undefined,
            shipTonDemand: sourcePlat.shipTonDemand || undefined
        } as Record<string, unknown>;
        // Vue“再来一单”保留当前登录人的联系方式；只有导入历史需求时才覆盖历史联系方式。
        if (!transport) {
            importedValues.contact = (source as any).contact;
            importedValues.mobile = (source as any).mobile;
        }
        form.setFieldsValue(importedValues as never);
        if (source.shipRouteId) void indexFreightService.findNewestDataByShipRouteId(source.shipRouteId).then(setPriceIndex);
    }, [form]);
    useEffect(() => {
        if (!repeatOrderId) return;
        void shipRequirementService.getOrderDetail({ orderId: repeatOrderId }).then(detail => {
            importOrderValues(detail.plat, detail.orderShips?.[0]);
        });
    }, [importOrderValues, repeatOrderId]);

    const indexUnitPrice = useMemo(() => {
        if (!priceIndex || !ton) return undefined;
        if (ton >= 6000 && ton <= 9000) return priceIndex.prices.sixToNineThousandPrice;
        if (ton === 10000) return priceIndex.prices.tenThousandPrice;
        if (ton >= 15000) return priceIndex.prices.fifteenUpThousandPrice;
        return undefined;
    }, [priceIndex, ton]);
    useEffect(() => {
        if (unitPriceType !== FREIGHT_RATE_TYPE.INDEX_PRICE.code) return;
        // 航线指数尚在请求时保留导入值；请求完成后再判断吨位是否落在指数区间。
        if (!priceIndex) return;
        if (indexUnitPrice) form.setFieldValue("unitPrice", indexUnitPrice);
        else form.setFieldsValue({ unitPriceType: undefined, unitPrice: undefined } as never);
    }, [form, indexUnitPrice, priceIndex, unitPriceType]);
    const submit = async (values: OrderFormValues) => {
        setSubmitting(true);
        try {
            const confirmed = await new Promise<boolean>(resolve => Modal.confirm({
                title: "提示",
                content: "是否发布，发布后平台将跟您确认，请留意",
                okText: "确定",
                cancelText: "取消",
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
            }));
            if (!confirmed) return;
            const selfPay = values.serviceType === SHIP_WAY_BILL_TYPE_PC.MATCHING.code || values.paymentMethod === SERVICE_PROVIDER_TYPE_PC.SELF.code;
            const payload = {
                ...values,
                depositPaymentMethod: selfPay ? DEPOSIT_PAYMENT_METHOD.PAY_ONESELF.code : values.depositPaymentMethod,
                dwtMin: values.shipTonDemand ? undefined : values.dwtMin,
                dwtMax: values.shipTonDemand ? undefined : values.dwtMax,
                loadDate: values.loadDate.format("YYYY-MM-DD"),
                orderType: 1
            };
            await shipRequirementService.add(payload);
            navigate("/logistics/shipOrder", { replace: true });
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <section className="ship-order-form-page">
            <Breadcrumb routes={[
                { name: "物流中心" },
                { name: "运单", path: "/logistics/shipOrder" },
                { name: "新增找船" }
            ]} />
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    serviceType: SHIP_WAY_BILL_TYPE_PC.SUPERVISION.code,
                    paymentMethod: SERVICE_PROVIDER_TYPE_PC.SERVICE.code,
                    shipRequirements: [],
                    maritimeAffairsFee: MARITIME_AFFAIRS_FEE.SHIPPERS_NOT_RESPONSIBLE.code,
                    orderType: 1,
                    replenishAppoint: ""
                }}
                onFinish={submit}
            >
                <div className="ship-order-info-card">
                    <h1>新增找船需求</h1>
                    <div className="ship-service-intro">
                        <strong>服务类型说明</strong>
                        <p>
                            1.选择&quot;服务运单”，您可享受船货匹配、运力管理、风险预警、在途跟踪(实时定位、船舶轨迹、可视化监控)、网络货运平台对接等服务
                        </p>
                        <p>2.选择&quot;撮合运单&quot;，平台仅提供船货匹配服务</p>
                    </div>
                    <div className="ship-order-form-grid">
                        <Form.Item name="serviceType" label="服务类型:" rules={[{ required: true }]}>
                            <Radio.Group
                                onChange={event => {
                                    const matching = event.target.value === SHIP_WAY_BILL_TYPE_PC.MATCHING.code;
                                    form.setFieldsValue(matching
                                        ? { paymentMethod: SERVICE_PROVIDER_TYPE_PC.SELF.code, maritimeAffairsFee: undefined, demurrageFee: undefined, deposit: undefined }
                                        : { paymentMethod: SERVICE_PROVIDER_TYPE_PC.SERVICE.code } as never);
                                }}
                                options={SHIP_WAY_BILL_TYPE_PC.options.map(item => ({
                                    label: item.label,
                                    value: Number(item.value)
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="paymentMethod"
                            label={
                                <span>
                                    船运费用支付方式{" "}
                                    <em onClick={() => setShowIntroduction(true)}>
                                        <i className="iconfont icon-Frame-2" />
                                        服务商垫付介绍
                                    </em>
                                </span>
                            }
                        >
                            {serviceType === 2 ? <span>自行支付</span> : <Radio.Group
                                options={SERVICE_PROVIDER_TYPE_PC.options.map(item => ({
                                    label: item.label,
                                    value: Number(item.value)
                                }))}
                            />}
                        </Form.Item>
                    </div>
                    {serviceType === 1 && paymentMethod === 1 ? <Form.Item name="depositPaymentMethod" rules={[{ required: true }]}><ShipProviderRadio options={providers} value={depositPaymentMethod} onChange={value => form.setFieldValue("depositPaymentMethod", value)} /></Form.Item> : null}
                </div>
                <div className="ship-order-info-card">
                    <h2 className="section-title">货物信息</h2>
                    <div className="ship-order-form-grid ship-goods-grid">
                        <Form.Item name="goodsType" label="货物类型" rules={[{ required: true }]}> 
                            <Input maxLength={32} placeholder="请输入货物类型" />
                        </Form.Item>
                        <ShipCommonGoods options={commonGoods} onSelect={value => form.setFieldValue("goodsType", value)} />
                        <Form.Item name="ton" label="运货吨数" rules={[{ required: true }]}>
                            <InputNumber
                                min={1}
                                max={1000000}
                                addonAfter="吨"
                                placeholder="请输入运货吨数"
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                        <Form.Item name="shipType" label="船型" rules={[{ required: true }]}>
                            <Select
                                placeholder="请选择船型"
                                options={FIND_SHIP_TYPE.options.map(item => ({
                                    label: item.label,
                                    value: Number(item.value)
                                }))}
                            />
                        </Form.Item>
                        <Form.Item name="shipRequirements" label="船舶要求">
                            <Select
                                mode="multiple"
                                placeholder="请选择船舶要求"
                                options={SHIP_REQUIREMENTS.options.map(item => ({
                                    label: item.label,
                                    value: Number(item.value)
                                }))}
                            />
                        </Form.Item>
                        <Form.Item name="drainageChannel" label="排水槽">
                            <Select
                                allowClear
                                placeholder="请选择排水槽"
                                options={DRAINAGE_CHANNEL.options.filter(item => Number(item.value) !== DRAINAGE_CHANNEL.NO_DRAINAGE_CHANNEL.code).map(item => ({
                                    label: item.label,
                                    value: Number(item.value)
                                }))}
                            />
                        </Form.Item>
                    </div>
                </div>
                <div className="ship-order-info-card">
                    <h2 className="section-title">运输信息</h2>
                    <div className="ship-order-form-grid">
                        <Form.Item name="sourcePortId" label="始发地" rules={[{ required: true }]}> 
                            <ShipRemotePortSelect onChange={(value, port) => {
                                const selected = Array.isArray(port) ? port[0] : port;
                                form.setFieldsValue({ sourcePortId: value, sourcePortName: selected ? selected.shortName || selected.name : undefined, shipRouteId: undefined, shipRouteSource: undefined } as never);
                                setRoutes([]);
                                setPriceIndex(undefined);
                            }} />
                        </Form.Item>
                        <Form.Item name="destinationPortId" label="目的地" rules={[{ required: true }]}> 
                            <ShipRemotePortSelect onChange={(value, port) => {
                                const selected = Array.isArray(port) ? port[0] : port;
                                form.setFieldsValue({ destinationPortId: value, destinationPortName: selected ? selected.shortName || selected.name : undefined, shipRouteId: undefined, shipRouteDestination: undefined } as never);
                                setRoutes([]);
                                setPriceIndex(undefined);
                            }} />
                        </Form.Item>
                        <div className="ship-combined-field"><label>船舶吨位要求</label><div><Form.Item name="shipTonDemand" valuePropName="checked" noStyle><Checkbox>{SHIO_TOP_DEMAND.SHIPPING.intro}</Checkbox></Form.Item>{!shipTonDemand ? <><Form.Item name="dwtMin" noStyle><InputNumber min={1} max={10000000000} addonAfter="吨" placeholder="最小吨位" /></Form.Item><Form.Item name="dwtMax" dependencies={["dwtMin"]} rules={[({ getFieldValue }) => ({ validator: (_, value) => !value || !getFieldValue("dwtMin") || Number(value) >= Number(getFieldValue("dwtMin")) ? Promise.resolve() : Promise.reject(new Error("最大吨位需大于等于最小吨位")) })]} noStyle><InputNumber min={1} max={10000000000} addonAfter="吨" placeholder="最大吨位" /></Form.Item></> : null}</div></div>
                        <div className="ship-combined-field"><label><i>*</i>期望装载日期</label><div><Form.Item name="loadDate" rules={[{ required: true }]} noStyle><DatePicker placeholder="请选择日期" /></Form.Item><Form.Item name="loadDays" rules={[{ required: true }]} noStyle><InputNumber min={0} max={30} addonAfter="天" placeholder="宽限天数" /></Form.Item></div></div>
                        <Form.Item name="loadUnloadDays" label="两港装卸天数" rules={[{ required: true }]}>
                            <InputNumber min={1} max={30} addonAfter="天" style={{ width: "100%" }} />
                        </Form.Item>
                        {sourcePortId && destinationPortId ? <Form.Item name="shipRouteId" label="航线名称"><Select placeholder="请选择航线" options={routes.map(route => ({ label: `${route.source}-${route.destination}`, value: route.id }))} onChange={value => { const route = routes.find(item => item.id === value); if (route) void selectRoute(route); }} /></Form.Item> : null}
                        {priceIndex ? <ShipPriceIndexCard value={priceIndex} /> : null}
                    </div>
                </div>
                <div className="ship-order-info-card">
                    <h2 className="section-title">费用信息</h2>
                    <div className="ship-order-form-grid">
                        <div className="ship-price-type-field"><label><i>*</i>意向运价</label><div><Form.Item name="unitPriceType" rules={[{ required: true }]} noStyle><Radio.Group
                                onChange={() => form.setFieldValue("unitPrice", undefined)}
                            >
                                <Radio value={FREIGHT_RATE_TYPE.INDEX_PRICE.code} disabled={!indexUnitPrice} onClick={() => { if (!indexUnitPrice) void message.error("当前始发地、目的地没有航线或该航线无指数价格"); }}>指数价格</Radio>
                                <Radio value={FREIGHT_RATE_TYPE.PRICE_IS_FIXED.code}>一口价</Radio>
                                <Radio value={FREIGHT_RATE_TYPE.CALL_FOR_PRICE.code}>电议</Radio>
                            </Radio.Group></Form.Item>
                            {unitPriceType === FREIGHT_RATE_TYPE.INDEX_PRICE.code ? <span className="ship-index-unit-price">{unitPrice}元/吨</span> : null}
                            {unitPriceType === FREIGHT_RATE_TYPE.PRICE_IS_FIXED.code ? <Form.Item name="unitPrice" rules={[{ required: true, message: "请输入意向运价" }, { type: "number", min: 0.01, max: 100000, message: "意向运价应大于0且小于等于10万" }]} noStyle><InputNumber
                                min={0}
                                max={100000}
                                precision={2}
                                controls={false}
                                addonAfter="元/吨"
                                placeholder="请输入意向运价"
                            /></Form.Item> : null}</div></div>
                        {unitPriceType !== FREIGHT_RATE_TYPE.CALL_FOR_PRICE.code ? <div className="ship-estimated-freight"><span>预估运费</span><strong>{unitPrice && ton ? `￥${Number(unitPrice * ton).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "--"}</strong></div> : null}
                        {serviceType === 1 ? (
                            <>
                                <Form.Item name="demurrageFee" label="可接受滞期费" rules={[{ required: true }]}> 
                                    <InputNumber min={0} max={100000} precision={2} addonAfter="元/吨/天" style={{ width: "100%" }} />
                                </Form.Item>
                                <Form.Item name="maritimeAffairsFee" label="海事费用" rules={[{ required: true }]}>
                                    <Select
                                        placeholder="请选择海事费用"
                                        options={MARITIME_AFFAIRS_FEE.options.map(item => ({
                                            label: item.label,
                                            value: Number(item.value)
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item name="deposit" label="船运定金">
                                    <InputNumber min={0.01} max={10000000} precision={2} addonAfter="元" style={{ width: "100%" }} />
                                </Form.Item>
                            </>
                        ) : null}
                    </div>
                </div>
                <div className="ship-order-info-card">
                    <h2 className="section-title">联系方式</h2>
                    <div className="ship-order-form-grid">
                        <Form.Item name="contact" label="联系人" rules={[{ required: true }]}>
                            <Input maxLength={32} placeholder="请输入联系人" />
                        </Form.Item>
                        <Form.Item name="mobile" label="手机号" rules={[{ required: true }, { pattern: /^1[3456789]\d{9}$/, message: "请输入有效手机号" }]}> 
                            <Input maxLength={32} placeholder="请输入手机号" />
                        </Form.Item>
                        <Form.Item className="ship-order-form-wide" name="replenishAppoint" label="补充约定">
                            <Input.TextArea maxLength={200} placeholder="如有其它说明, 请补充" />
                        </Form.Item>
                    </div>
                </div>
            </Form>
            <Space className="detail-buttons ship-order-form-actions">
                <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                    发布
                </Button>
                {lastOrder?.id ? <Button type="primary" onClick={() => importOrderValues(lastOrder)}>导入上次找船数据</Button> : null}
                <Button type="primary" onClick={() => navigate("/logistics/shipOrder")}>返回</Button>
            </Space>
            {showIntroduction ? <ShipPaymentIntroduction onClose={() => setShowIntroduction(false)} /> : null}
        </section>
    );
}

/** 运单详情同时支持 requireId 与正式 orderId，并按运输阶段展示服务端 VO。 */
export function ShipOrderDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const requireId = params.get("requireId") ?? "";
    const [info, setInfo] = useState<ShippingRequirementCustomerVo>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeDetailTab, setActiveDetailTab] = useState<"transportInfo" | "inTransitSupervision">("transportInfo");
    // Vue 使用 v-if + v-show：监管页签首次访问后保留挂载，切回运输信息时不能丢失筛选和选中状态。
    const [hasVisitedInTransitSupervision, setHasVisitedInTransitSupervision] = useState(false);
    const load = async () => {
        if (!id && !requireId) {
            setError(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            setInfo(
                await shipRequirementService.getOrderDetail(id ? { orderId: id } : { requireId: Number(requireId) })
            );
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        setActiveDetailTab("transportInfo");
        setHasVisitedInTransitSupervision(false);
        void load();
    }, [id, requireId]);
    // 旧 Vue 运单详情只延迟数据区块渲染，不展示全屏加载框。
    if (loading) return null;
    if (error || !info)
        return (
            <Alert
                type="error"
                showIcon
                message={id || requireId ? "运单详情加载失败" : "缺少运单 ID"}
                action={id || requireId ? <Button onClick={() => void load()}>重试</Button> : undefined}
            />
        );
    const order = info.orderShipVos?.[0];
    const transport = order?.transportOrderShip;
    // 状态接口可能返回数字字符串，详情展示与 Vue Meta 查询统一使用数值状态。
    const orderState = Number(info.tarnsPortOrderState);
    const showSupervision = Boolean(transport && transport.serviceType === SHIP_WAY_BILL_TYPE_PC.SUPERVISION.code);
    const matching = transport?.serviceType === SHIP_WAY_BILL_TYPE_PC.MATCHING.code;
    const cancelled = SHIPPING_REQUIREMENT_CUSTOMER_TRANSPORT_ORDER_STATE.CANCEL.match(orderState);
    const stage = getShipOrderStage(transport?.state);
    const showMonitor = Boolean(order?.shipVo?.devices?.length && transport && ![10, 7, 8].includes(transport.state));
    return (
        <section className={`ship-order-detail-page${activeDetailTab === "inTransitSupervision" ? " ship-order-detail-page--supervision" : ""}`}>
            <DetailHeader
                title="运单详情"
                leftInfos={[{ label: "编号", value: transport?.id ?? info.title }]}
                menu={<Breadcrumb routes={[{ name: "物流中心" }, { name: "运单", path: "/logistics/shipOrder" }, { name: "运单详情" }]} />}
                quoteCount={{ history: cancelled, value: info.quoteCount }}
                rightInfos={[{ label: "发布时间", value: info.publishTime ? dayjs(info.publishTime).format("YYYY-MM-DD HH:mm") : "--" }]}
                state={{ text: SHIPPING_REQUIREMENT_CUSTOMER_TRANSPORT_ORDER_STATE.getIntroByCode(orderState) ?? undefined }}
                tabs={showSupervision ? <ZhInsetTabs active={activeDetailTab} onChange={value => {
                    const nextTab = value as typeof activeDetailTab;
                    setActiveDetailTab(nextTab);
                    if (nextTab === "inTransitSupervision") setHasVisitedInTransitSupervision(true);
                }} options={[{ label: "运输信息", value: "transportInfo" }, { label: "在途监管", value: "inTransitSupervision" }]} /> : undefined}
            />
            <div hidden={activeDetailTab !== "transportInfo"}><div className="ship-order-detail-content" id="ship-order-box"><div className="ship-order-detail-left">{cancelled ? <ShipOrderCancelInfo info={info} /> : null}{order ? <ShipOrderBaseInfo info={order} /> : null}{order && !matching && stage >= 1 ? <ShipOrderLoadedInfo info={order} /> : null}{order && !matching && stage >= 2 ? <ShipOrderDepartureInfo info={order} /> : null}{order && !matching && stage >= 4 ? <ShipOrderDischargedInfo info={order} /> : null}{info.plat?.id && stage === 0 && !matching ? <ShipRequirementInfo info={info.plat} state={orderState} /> : null}<ShipOrderOperationHistory bizNo={id || requireId} /></div>{order && !matching && (stage >= 3 || showMonitor) ? <aside className="ship-order-detail-right"><ShipOrderMonitor info={order} />{stage >= 3 ? <ShipOrderTransportationInfo info={order} state={orderState} /> : null}</aside> : null}</div></div>
            {hasVisitedInTransitSupervision && showSupervision ? <div hidden={activeDetailTab !== "inTransitSupervision"}><InTransitPanel transportOrderShipId={String(transport?.id ?? id)} shipName={order?.shipVo?.ship?.cnname || order?.shipVo?.ship?.name} /></div> : null}
            <ShipOrderActions info={info} refresh={load} onBack={() => navigate("/logistics/shipOrder")} onRepeat={repeatId => navigate(`/logistics/shipOrder/edit?id=${repeatId}`)} />
        </section>
    );
}
